import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppStore } from "@/stores";
import { getDealOwner, getFullName, getUserIdsForTeam } from "@revelate/utils";
import { CircleAlert } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { HealthRiskRecordsTable } from "../pipeline-health-l2/records";
import { RevenueIntelligenceChatRegistration } from "../components/RevenueIntelligenceChatRegistration";
import { RevenueIntelligenceChatScopeRegistration } from "../components/RevenueIntelligenceChatScopeRegistration";
import {
  ForecastDiagnosis,
  type ForecastDependencyItem,
} from "../forecast-diagnosis/ForecastDiagnosis";
import { useForecastDiagnosisStory } from "../forecast-diagnosis/useForecastDiagnosisStory";
import { getChatRegistrationFromAnalysisContract } from "../lib/revenue-intelligence-analysis-contract";
import { REVENUE_INTELLIGENCE_COPY } from "../lib/revenueIntelligenceCopy";
import { AnalysisNarrative } from "./AnalysisNarrative";
import { ForecastFilters } from "./ForecastFilters";
import { ForecastPosition, type ForecastChartRow } from "./ForecastChart";
import {
  formatForecastShare,
} from "./forecastInsightCopy";
import {
  getDealForecastContribution,
  getTopForecastDealDependency,
  getTopForecastConcentration,
  getTopForecastMembershipConcentration,
  type ForecastConcentration,
  type ForecastDealDependency,
} from "./forecastInsights";
import {
  buildForecastOverviewChatContract,
  getForecastSummary,
} from "./forecastOverviewChatContract";
import { getStateOfSalesV2ForecastPosition } from "./forecastPosition";
import {
  buildStateOfSalesChatScopeActions,
  getStateOfSalesChatFiltersLabel,
} from "./stateOfSalesChatScope";
import {
  buildStateOfSalesV2Query,
  useStateOfSalesV2Forecast,
} from "./useForecastData";

type ForecastModel = ReturnType<typeof useStateOfSalesV2Forecast>;
type ForecastData = NonNullable<ForecastModel["forecast"]>;
type ForecastDeal = ForecastData["stateOfSalesV2OpenDeals"][number];
type ForecastDependency = ForecastConcentration<ForecastDeal> | ForecastDealDependency<ForecastDeal>;

function ForecastPageLoading() {
  return (
    <div className="flex min-h-80 items-center justify-center" aria-busy="true" aria-label="Loading Forecast">
      <Loader size={32} parentClassName="" />
    </div>
  );
}

function ForecastPageUnavailable({ retry }: { retry: () => Promise<unknown> }) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
        <CircleAlert className="h-5 w-5 text-gray-400" />
        <div>
          <p className="text-sm font-medium">Forecast data could not be loaded</p>
          <p className="mt-1 text-xs text-gray-500">A required Forecast source could not be reached.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void retry()}>Try again</Button>
      </CardContent>
    </Card>
  );
}

function uniqueDealsWithForecastContribution<Deal extends { forecast_contribution?: number | null; id: number | string }>(
  deals: readonly Deal[],
) {
  return Array.from(
    new Map(
      deals
        .filter(deal => getDealForecastContribution(deal) > 0)
        .map(deal => [String(deal.id), deal]),
    ).values(),
  );
}

export function RevenueIntelligenceStateOfSalesV2ForecastPage() {
  const model = useStateOfSalesV2Forecast({ view: "forecast" });
  const story = useForecastDiagnosisStory(model);
  const currentUser = useAppStore(state => state.currentUser);
  const [, setLocation] = useLocation();
  const [selectedConcentration, setSelectedConcentration] = useState<ForecastDependency | null>(null);

  if (model.isLoading) return <ForecastPageLoading />;
  if (model.error || !model.forecast || !story) {
    return <ForecastPageUnavailable retry={model.retry} />;
  }

  const forecast = model.forecast;
  const forecastPosition = getStateOfSalesV2ForecastPosition(
    forecast,
    model.period,
    model.accumulated,
    model.chartData,
  );
  const summary = getForecastSummary(
    forecast,
    forecastPosition,
    model.companyCurrency,
    !model.expectedCloseV2Available,
  );
  const selectedPeriodMonths = new Set(
    model.chartData
      .filter(row => !row.is_context)
      .map(row => row.month),
  );
  const forecastContributingDeals = uniqueDealsWithForecastContribution(
    model.getForecastDealsForMonths(selectedPeriodMonths)
      .filter(deal => !deal.is_closed && !deal.is_won && !deal.is_lost),
  ).map(deal => ({
    ...deal,
    forecast_share: forecastPosition.forecast > 0
      ? getDealForecastContribution(deal) / forecastPosition.forecast
      : null,
  }));

  const ownerConcentration = getTopForecastConcentration(
    forecastContributingDeals,
    forecastPosition.forecast,
    "sales_rep",
    "Sales rep",
    (deal) => {
      const owner = getDealOwner(deal);
      return owner ? getFullName(owner) : "Not specified";
    },
  );
  const teamConcentration = getTopForecastMembershipConcentration(
    forecastContributingDeals,
    forecastPosition.forecast,
    "team",
    "Team",
    model.teams.map(team => {
      const teamOwnerIds = new Set(getUserIdsForTeam(model.teams, team.id, true));
      return {
        dealIds: forecastContributingDeals
          .filter(deal => {
            const ownerId = getDealOwner(deal)?.id;
            return ownerId ? teamOwnerIds.has(ownerId) : false;
          })
          .map(deal => deal.id),
        id: team.id,
        label: team.name,
      };
    }),
  );
  const stageConcentration = getTopForecastConcentration(
    forecastContributingDeals,
    forecastPosition.forecast,
    "stage",
    "Pipeline stage",
    deal => deal.stage_label ?? "Not specified",
  );
  const pipelineConcentration = getTopForecastConcentration(
    forecastContributingDeals,
    forecastPosition.forecast,
    "pipeline",
    "Pipeline",
    deal => deal.pipeline_label ?? "Not specified",
  );
  const keyDealDependency = getTopForecastDealDependency(
    forecastContributingDeals,
    forecastPosition.forecast,
  );
  const dependencyCandidates: Array<{
    concentration: ForecastDependency | null;
    dimension: string;
  }> = [
    { concentration: keyDealDependency, dimension: REVENUE_INTELLIGENCE_COPY.forecast.keyDeals },
    { concentration: ownerConcentration, dimension: "Sales rep" },
    { concentration: teamConcentration, dimension: "Team" },
    { concentration: stageConcentration, dimension: "Pipeline stage" },
    { concentration: pipelineConcentration, dimension: "Pipeline" },
  ];
  const concentrations = dependencyCandidates.filter((item): item is {
    concentration: ForecastDependency;
    dimension: string;
  } => item.concentration !== null);
  const forecastDependencies: ForecastDependencyItem[] = concentrations.map(item => ({
    dealCount: item.concentration.dealCount,
    dimension: item.dimension,
    id: `${item.concentration.key}:${item.dimension}`,
    label: item.concentration.label,
    share: item.concentration.share,
    value: item.concentration.value,
  }));

  const forecastChatContract = buildForecastOverviewChatContract({
    accumulated: model.accumulated,
    chartData: model.chartData as ForecastChartRow[],
    companyCurrency: model.companyCurrency,
    companyId: String(currentUser?.company_id ?? "unavailable"),
    filtersLabel: getStateOfSalesChatFiltersLabel(model),
    forecast,
    period: model.period,
    movementComparable: !model.expectedCloseV2Available,
  });
  const resolveChatContracts = (filters: typeof model.filters) => {
    const scopedForecast = model.evaluateForecastForFilters(filters);
    if (!scopedForecast) return [];
    const contract = buildForecastOverviewChatContract({
      accumulated: filters.accumulated,
      chartData: model.buildChartDataForFilters(scopedForecast, filters) as ForecastChartRow[],
      companyCurrency: model.companyCurrency,
      companyId: String(currentUser?.company_id ?? "unavailable"),
      filtersLabel: getStateOfSalesChatFiltersLabel({
        ...model,
        selectedOwners: filters.owners,
        selectedPipelines: filters.pipelines,
        selectedTeams: filters.teams,
      }),
      forecast: scopedForecast,
      period: filters.period,
      movementComparable: !model.expectedCloseV2Available,
    });
    const registration = getChatRegistrationFromAnalysisContract(contract);
    return registration ? [registration] : [];
  };
  const dealContext = new URLSearchParams(buildStateOfSalesV2Query(model.filters));
  dealContext.set("dealDetailsViewSource", "state-of-sales-v2");
  const openDeal = (dealId: number | string) => setLocation(
    `/revenue-intelligence/deal/${dealId}?${dealContext.toString()}`,
  );

  return (
    <div className="space-y-8 pb-10">
      <RevenueIntelligenceChatRegistration contract={forecastChatContract} />
      <RevenueIntelligenceChatScopeRegistration
        actions={buildStateOfSalesChatScopeActions(model, resolveChatContracts)}
        scopeKey="state_of_sales.filters"
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <h1 className="text-2xl font-semibold">Forecast</h1>
        <ForecastFilters model={model} />
      </div>

      <AnalysisNarrative
        conclusion={summary.conclusion}
        label="Executive summary"
      />

      <Card>
        <CardContent className="p-5">
          <ForecastPosition
            position={forecastPosition}
            companyCurrency={model.companyCurrency}
            expectedLabel="Expected from open deals"
            showBar={false}
            targetUnavailable={forecastPosition.target <= 0}
          />
        </CardContent>
      </Card>

      <ForecastDiagnosis
        forecastDependencies={forecastDependencies}
        onOpenDeal={openDeal}
        onOpenForecastDependency={(dependencyId) => {
          const dependency = concentrations.find(item => (
            `${item.concentration.key}:${item.dimension}` === dependencyId
          ));
          if (dependency) setSelectedConcentration(dependency.concentration);
        }}
        showForecastContext={false}
        story={story}
      />

      <Dialog open={selectedConcentration !== null} onOpenChange={open => !open && setSelectedConcentration(null)}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{selectedConcentration?.label ?? REVENUE_INTELLIGENCE_COPY.forecast.forecastDependencies}</DialogTitle>
            <DialogDescription>
              {selectedConcentration
                ? selectedConcentration.key === "deals"
                  ? `${formatForecastShare(selectedConcentration.share)} of Forecast from these ${selectedConcentration.dealCount} ${selectedConcentration.dealCount === 1 ? "Deal" : "Deals"}.`
                  : `${formatForecastShare(selectedConcentration.share)} of Forecast in ${selectedConcentration.dimension.toLowerCase()}, across ${selectedConcentration.dealCount} contributing ${selectedConcentration.dealCount === 1 ? "Deal" : "Deals"}.`
                : "Deals contributing to this Forecast dependency."}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            <HealthRiskRecordsTable
              deals={selectedConcentration?.deals ?? []}
              currency={model.companyCurrency}
              columnOptions={{ showExpectedCloseDate: true, showForecastImpact: true,
                forecastValueDisplay: "contribution", showForecastShare: true }}
              onOpenDeal={openDeal}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
